import {Grid} from 'src/design/atoms/Grid';
import React from 'react';
import {FormProvider, useFormState} from 'react-hook-form';
import type {ValidationErrors} from 'src/app/forms/FormValidationSummary';
import {FormValidationSummary} from 'src/app/forms/FormValidationSummary';
import {ProactField} from './ProactField';
import {ProactFormContext, useProactFormContext} from './ProactFormContext';
import type {ProactFormApi} from './useProactForm';

export interface ProactFormProps {
  form: ProactFormApi;
  children: React.ReactNode;
}

/**
 * Provider for a metadata-driven form. Hosts react-hook-form context plus the
 * Proact form API so <ProactField> / <ProactFormGrid> can be composed into
 * any layout.
 */
export const ProactForm: React.FC<ProactFormProps> = ({form, children}) => (
  <FormProvider {...form.methods}>
    <ProactFormContext.Provider value={form}>
      {children}
    </ProactFormContext.Provider>
  </FormProvider>
);

export interface ProactFormGridProps {
  spacing?: 'compact' | 'normal';
  showValidationSummary?: boolean;
  /** Selects rendered fields in metadata order; validation still covers the whole form. */
  fieldNames?: readonly string[];
  mb?: React.ComponentProps<typeof Grid>['mb'];
}

/**
 * Standard grid layout rendering all or selected metadata fields in order.
 */
export const ProactFormGrid: React.FC<ProactFormGridProps> = ({
  spacing = 'normal',
  showValidationSummary = false,
  fieldNames,
  mb = spacing === 'compact' ? 'xs' : 'xl',
}) => {
  const {formMeta} = useProactFormContext();
  const fields =
    fieldNames === undefined
      ? formMeta
      : formMeta.filter(field => fieldNames.includes(field.name));

  return (
    <>
      <Grid
        data-testid="form-edit-container"
        mb={mb}
        gutter={spacing === 'compact' ? 'xs' : 'md'}
        maw={{base: '100%', xl: '1200px'}}>
        {fields.map(field => (
          <ProactField key={field.name} name={field.name} />
        ))}
      </Grid>
      {showValidationSummary && <ProactFormValidationSummary />}
    </>
  );
};

/**
 * Bridges react-hook-form error state into the existing FormValidationSummary
 * component. Subscribes to errors only.
 */
export const ProactFormValidationSummary: React.FC = () => {
  const {formMeta, methods} = useProactFormContext();
  const {errors} = useFormState({control: methods.control});

  const flatErrors: ValidationErrors = {};
  Object.entries(errors).forEach(([name, error]) => {
    if (error?.message) {
      flatErrors[name] = String(error.message);
    }
  });

  return <FormValidationSummary errors={flatErrors} formMeta={formMeta} />;
};
